import { Margin, PrimaryCTALink } from '@ovotech/nebula';
import { useAccountContext } from '@ovotech/ui-tools';
import React from 'react';
import RecommendedDirectDebitInfo from './RecommendedDirectDebitInfo/RecommendedDirectDebitInfo';
import selector from './selector';
import {
  ErrorSection,
  PageWithNavigation,
  RouterLink,
  Section,
} from '@/src/components';
import { RECOMMENDED_DD_ERROR } from '@/src/constants/error-section-ids';
import { ROUTE_CHANGE_DD_AMOUNT } from '@/src/constants/routes';
import { RECOMMENDED_DD_SECTION_ID } from '@/src/constants/sectionIds';
import {
  useContractsQueryLegacy,
  useRecommendedDirectDebitQuery,
} from '@/src/api';
import {
  ContractsResponse,
  RecommendedDirectDebitResponse,
} from '@/src/types/Response';
import { SkeletonLoader } from '@/src/components/ActionCard/components/SkeletonLoader';

const RecommendedDirectDebit = ({
  data: [recommendedDirectDebit, contracts],
}: {
  data: readonly [RecommendedDirectDebitResponse, ContractsResponse];
}) => {
  const data = selector({ contracts, recommendedDirectDebit });

  return (
    <PageWithNavigation title="Recommended Direct Debit">
      <Section id={RECOMMENDED_DD_SECTION_ID}>
        <Margin bottom={6}>
          <RecommendedDirectDebitInfo {...data} />
          <PrimaryCTALink to={ROUTE_CHANGE_DD_AMOUNT} as={RouterLink}>
            Change Direct Debit amount
          </PrimaryCTALink>
        </Margin>
      </Section>
    </PageWithNavigation>
  );
};

RecommendedDirectDebit.displayName = 'RecommendedDirectDebit';

const Error = () => (
  <ErrorSection alertLevel="warn" id={RECOMMENDED_DD_ERROR} />
);

const RecommendedDirectDebitWithData = ({
  accountId,
}: {
  accountId: string;
}) => {
  const recommendedDDResult = useRecommendedDirectDebitQuery();
  const contractsResult = useContractsQueryLegacy(accountId);

  if (recommendedDDResult.isLoading || contractsResult.isLoading) {
    return <SkeletonLoader dataTestId="RecommendedDirectDebitWithData" />;
  }

  if (
    recommendedDDResult.isError ||
    contractsResult.isError ||
    !recommendedDDResult.data ||
    !contractsResult.data
  ) {
    return <Error />;
  }

  return (
    <RecommendedDirectDebit
      data={[recommendedDDResult.data, contractsResult.data]}
    />
  );
};

const RecommendedDirectDebitMain = () => {
  const { accountId } = useAccountContext();
  if (!accountId) {
    return null;
  }
  return <RecommendedDirectDebitWithData accountId={accountId} />;
};

export default RecommendedDirectDebitMain;
